// "dracarys" easter egg — a rage-release: the current Backoffice screen appears to burn away from the
// bottom up, chars to ash, then rebuilds good as new. Purely cosmetic — a full-screen canvas overlay
// covers the page and nothing in the DOM, data, or backend is ever touched. Self-cleaning like snow.ts.
//
// A blue-fire variant fires when snow is already falling (see EasterEggs.tsx): the secret third effect,
// "a song of ice and fire". No dependencies.
const Z = 9999;
const STEP = 16; // column width for the wavy burn frontier / flames
const BURN_MS = 4200; // time for the fire to climb from bottom to top
const SHAKE_AT = 2500; // when the screen-shake kicks in
const SHAKE_MS = 700;

interface Palette { char: [string, string]; outer: string; mid: string; core: string; ember: string; smoke: string; }
const ORANGE: Palette = { char: ['#1a0d05', '#050302'], outer: '#ff3b00', mid: '#ff9a00', core: '#fff2b0', ember: '#ff7a1a', smoke: '#2b2622' };
const BLUE: Palette = { char: ['#050d1a', '#020409'], outer: '#1e6bff', mid: '#49b8ff', core: '#dff3ff', ember: '#5fd0ff', smoke: '#232a38' };

interface Ember { x: number; y: number; vy: number; vx: number; life: number; max: number; r: number; }
interface Smoke { x: number; y: number; vy: number; vx: number; life: number; max: number; r: number; }

let canvas: HTMLCanvasElement | null = null;
let ctx: CanvasRenderingContext2D | null = null;
let panel: HTMLDivElement | null = null;
let embers: Ember[] = [];
let smoke: Smoke[] = [];
let raf = 0;
let W = 0;
let H = 0;
let dpr = 1;
let startedAt = 0;
let phase = 0;
let done = false; // reached full ash + panel shown
let restoring = false;
let shook = false;
let pal: Palette = ORANGE;
let onRestored: (() => void) | null = null;
let prevOverflow = ''; // the host's inline <html> overflow, restored on teardown

// Each flame "tongue" is an independent oscillator across the width, so the fire licks and dances
// instead of undulating as one block. Rebuilt on resize.
interface Tongue { x: number; base: number; w: number; seed: number; }
let tongues: Tongue[] = [];

// Soft glowing embers scattered over the ash once the screen has burned down — they breathe so the
// aftermath keeps smouldering instead of going flat and dead.
interface Glow { x: number; y: number; r: number; seed: number; }
let ashGlow: Glow[] = [];

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') rebuild();
};

const buildTongues = () => {
    tongues = [];
    // Slightly irregular spacing + overlap so the flame front never looks like a picket fence.
    for (let x = -10; x <= W + 20; x += rnd(14, 24)) {
        tongues.push({ x, base: rnd(110, 230), w: rnd(11, 22), seed: Math.random() * 100 });
    }
};

const buildAshGlow = () => {
    ashGlow = [];
    const count = Math.max(14, Math.round(W / 26));
    for (let i = 0; i < count; i++) {
        ashGlow.push({ x: Math.random() * W, y: rnd(H * 0.35, H), r: rnd(22, 64), seed: Math.random() * 100 });
    }
};

const resize = () => {
    if (!canvas || !ctx) return;
    W = window.innerWidth;
    H = window.innerHeight;
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = `${W}px`;
    canvas.style.height = `${H}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    buildTongues();
};

// The uneven, licking top edge of the fire at column x — a couple of sines plus per-column flicker.
const frontierAt = (burnY: number, x: number): number =>
    burnY + Math.sin(x * 0.03 + phase * 2) * 10 + Math.sin(x * 0.11 + phase * 3.3) * 6;

// Organic per-tongue flicker: several sines at incommensurate rates keyed by the tongue's seed, so no
// two tongues pulse together. Returns a 0..1-ish factor (clamped ≥ 0.15 so a lick never fully dies).
const flicker = (seed: number, t: number): number => {
    const n = 0.55
        + 0.32 * Math.sin(t * 3.1 + seed * 1.7)
        + 0.22 * Math.sin(t * 5.7 + seed * 0.9)
        + 0.14 * Math.sin(t * 9.3 + seed * 2.3);
    return Math.max(0.15, n);
};

// One tapering flame lick: a bezier tongue from the frontier up to a swaying tip, filled with a
// vertical gradient. Drawn additively so overlapping layers build up heat.
const drawTongue = (x: number, footY: number, h: number, w: number, sway: number, tip: string, mid: string, foot: string) => {
    if (!ctx) return;
    const tipX = x + sway;
    ctx.beginPath();
    ctx.moveTo(x - w, footY);
    ctx.bezierCurveTo(x - w * 0.7, footY - h * 0.45, tipX - w * 0.28, footY - h * 0.85, tipX, footY - h);
    ctx.bezierCurveTo(tipX + w * 0.28, footY - h * 0.85, x + w * 0.7, footY - h * 0.45, x + w, footY);
    ctx.closePath();
    const g = ctx.createLinearGradient(0, footY - h, 0, footY);
    g.addColorStop(0, tip);
    g.addColorStop(0.55, mid);
    g.addColorStop(1, foot);
    ctx.fillStyle = g;
    ctx.fill();
};

const loop = () => {
    raf = requestAnimationFrame(loop);
    if (!ctx) return;
    phase += 0.016;
    const elapsed = Date.now() - startedAt;
    const t = Math.min(1, elapsed / BURN_MS);
    // Ease-in so it starts as a smoulder and races up as it catches.
    const eased = t * t;
    const burnY = done ? -20 : H - eased * (H + 40);

    ctx.clearRect(0, 0, W, H);

    // 1) Charred region: everything below the frontier is consumed — opaque, so the page vanishes.
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (let x = 0; x <= W; x += STEP) ctx.lineTo(x, frontierAt(burnY, x));
    ctx.lineTo(W, H);
    ctx.closePath();
    const cg = ctx.createLinearGradient(0, burnY - 30, 0, H);
    cg.addColorStop(0, pal.char[0]);
    cg.addColorStop(1, pal.char[1]);
    ctx.fillStyle = cg;
    ctx.fill();

    // 2) Flames licking up from the frontier — additive so overlapping layers build heat.
    ctx.globalCompositeOperation = 'lighter';

    // A soft hot bed along the whole frontier so gaps between tongues still glow.
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (let x = 0; x <= W; x += STEP) ctx.lineTo(x, frontierAt(burnY, x));
    ctx.lineTo(W, H);
    ctx.closePath();
    const bed = ctx.createLinearGradient(0, burnY - 34, 0, burnY + 10);
    bed.addColorStop(0, `${pal.outer}00`);
    bed.addColorStop(1, pal.mid);
    ctx.fillStyle = bed;
    ctx.fill();

    // Breathing ember glow over the ash (only once burned down).
    if (done) {
        for (const gl of ashGlow) {
            const a = 0.05 + 0.06 * (0.5 + 0.5 * Math.sin(phase * 1.6 + gl.seed));
            ctx.globalAlpha = a;
            const rg = ctx.createRadialGradient(gl.x, gl.y, 0, gl.x, gl.y, gl.r);
            rg.addColorStop(0, pal.ember);
            rg.addColorStop(1, `${pal.ember}00`);
            ctx.fillStyle = rg;
            ctx.beginPath();
            ctx.arc(gl.x, gl.y, gl.r, 0, Math.PI * 2);
            ctx.fill();
        }
    }

    // Three layers per tongue: a tall wide outer body, a shorter orange mid, a small white-hot core.
    for (const tg of tongues) {
        const footY = frontierAt(burnY, tg.x);
        const f = flicker(tg.seed, phase);
        const h = tg.base * f;
        const sway = Math.sin(phase * 2.1 + tg.seed) * h * 0.14;
        ctx.globalAlpha = 0.42;
        drawTongue(tg.x, footY, h, tg.w * 1.35, sway, `${pal.outer}00`, `${pal.outer}cc`, pal.outer);
        ctx.globalAlpha = 0.6;
        drawTongue(tg.x, footY, h * 0.68, tg.w * 0.85, sway * 0.9, `${pal.mid}00`, `${pal.mid}dd`, pal.mid);
        ctx.globalAlpha = 0.85;
        drawTongue(tg.x, footY, h * 0.36, tg.w * 0.48, sway * 0.8, `${pal.core}00`, `${pal.core}ee`, pal.core);
    }

    // 3) Embers rising off the fire — a full stream while burning, a faint trickle off the ash after.
    if (embers.length < 160) {
        const spawn = done ? (Math.random() < 0.4 ? 1 : 0) : 3;
        for (let i = 0; i < spawn; i++) {
            const x = Math.random() * W;
            const y = done ? rnd(H * 0.55, H) : frontierAt(burnY, x);
            embers.push({ x, y, vy: rnd(-2.6, -0.9), vx: rnd(-0.6, 0.6), life: 0, max: rnd(40, 110), r: rnd(0.8, 2.6) });
        }
    }
    for (const e of embers) {
        e.life++;
        e.y += e.vy;
        e.x += e.vx;
        e.vy *= 0.99;
        const k = 1 - e.life / e.max;
        ctx.globalAlpha = Math.max(0, k) * 0.9;
        ctx.fillStyle = pal.ember;
        ctx.beginPath();
        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
        ctx.fill();
    }
    embers = embers.filter((e) => e.life < e.max && e.y > -10);

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;

    // 4) Smoke haze drifting up ahead of the flames — grey, obscuring (not additive), so it reads as
    //    haze over the still-visible page above the fire front.
    if (smoke.length < 46) {
        const spawn = done ? (Math.random() < 0.2 ? 1 : 0) : (Math.random() < 0.7 ? 1 : 0);
        for (let i = 0; i < spawn; i++) {
            const x = Math.random() * W;
            smoke.push({ x, y: frontierAt(burnY, x) - rnd(0, 40), vy: rnd(-1.9, -0.9), vx: rnd(-0.5, 0.5), life: 0, max: rnd(70, 150), r: rnd(26, 60) });
        }
    }
    for (const s of smoke) {
        s.life++;
        s.y += s.vy;
        s.x += s.vx;
        s.vy *= 0.996;
        const k = s.life / s.max;
        const grow = s.r * (1 + k * 1.6);
        ctx.globalAlpha = Math.sin(Math.min(1, k) * Math.PI) * 0.16; // fade in then out
        const rg = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, grow);
        rg.addColorStop(0, pal.smoke);
        rg.addColorStop(1, `${pal.smoke}00`);
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.arc(s.x, s.y, grow, 0, Math.PI * 2);
        ctx.fill();
    }
    smoke = smoke.filter((s) => s.life < s.max && s.y > -90);
    ctx.globalAlpha = 1;

    // Screen shake at peak intensity.
    if (!shook && !reduced() && elapsed >= SHAKE_AT) {
        shook = true;
        shake();
    }

    // Reached the top → settle to ash and offer the rebuild.
    if (!done && t >= 1) {
        done = true;
        buildAshGlow();
        window.setTimeout(showPanel, 500);
    }
};

// Body shake via an injected keyframe (guarded by reduced-motion above).
const shake = () => {
    const style = document.createElement('style');
    style.id = 'dracarys-shake';
    style.textContent =
        '@keyframes dracarysShake{0%,100%{transform:translate(0,0)}20%{transform:translate(-4px,3px)}40%{transform:translate(5px,-2px)}60%{transform:translate(-3px,-4px)}80%{transform:translate(4px,2px)}}' +
        'body.dracarys-shaking{animation:dracarysShake 90ms linear 0s infinite}';
    document.head.appendChild(style);
    document.body.classList.add('dracarys-shaking');
    window.setTimeout(() => {
        document.body.classList.remove('dracarys-shaking');
        style.remove();
    }, SHAKE_MS);
};

let panelTitle = '🔥 RAGE RELEASE COMPLETE';
let panelSubtitle = 'The Backoffice has been cleansed by fire. No data was harmed in the process.';

const showPanel = () => {
    if (panel) return;
    panel = document.createElement('div');
    panel.style.cssText =
        `position:fixed;inset:0;z-index:${Z + 1};display:flex;flex-direction:column;align-items:center;justify-content:center;` +
        'text-align:center;padding:24px;opacity:0;transition:opacity 900ms ease;font-family:system-ui,sans-serif;';
    panel.innerHTML =
        `<div style="font:700 40px system-ui,sans-serif;letter-spacing:2px;color:${pal.core};text-shadow:0 2px 30px ${pal.outer};">${panelTitle}</div>` +
        `<div style="margin-top:14px;max-width:520px;font:400 18px system-ui,sans-serif;color:#cfd6e4;">${panelSubtitle}</div>` +
        `<button id="dracarys-rebuild" style="margin-top:28px;padding:12px 26px;font:600 16px system-ui,sans-serif;cursor:pointer;` +
        `color:#0a0a0a;background:${pal.mid};border:none;border-radius:8px;box-shadow:0 6px 30px ${pal.outer};">Rebuild from the ashes</button>`;
    document.body.appendChild(panel);
    panel.querySelector<HTMLButtonElement>('#dracarys-rebuild')?.addEventListener('click', rebuild);
    requestAnimationFrame(() => { if (panel) panel.style.opacity = '1'; });

    // A restless little wobble + glow-pulse to draw the eye to the way out (skipped for reduced-motion).
    if (!reduced()) {
        const style = document.createElement('style');
        style.id = 'dracarys-btn';
        style.textContent =
            '@keyframes dracarysBtn{0%,100%{transform:translateX(0)}25%{transform:translateX(-3px)}50%{transform:translateX(3px)}75%{transform:translateX(-2px)}}' +
            '#dracarys-rebuild{animation:dracarysBtn 120ms linear infinite}';
        document.head.appendChild(style);
    }
};

const teardown = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    window.removeEventListener('resize', resize);
    window.removeEventListener('keydown', onKey);
    document.body.classList.remove('dracarys-shaking');
    document.getElementById('dracarys-shake')?.remove();
    document.getElementById('dracarys-btn')?.remove();
    document.documentElement.style.overflow = prevOverflow;
    canvas?.remove();
    canvas = null;
    ctx = null;
    panel?.remove();
    panel = null;
    embers = [];
    smoke = [];
    ashGlow = [];
    done = false;
    restoring = false;
    shook = false;
    const cb = onRestored;
    onRestored = null;
    cb?.();
};

// Rebuild the screen from the ashes: a quick puff, then everything is back exactly as it was.
function rebuild(): void {
    if (!canvas || restoring) return;
    restoring = true;
    if (panel) panel.style.opacity = '0';
    canvas.style.transition = 'opacity 700ms ease, transform 700ms ease';
    canvas.style.transformOrigin = 'center center';
    canvas.style.opacity = '0';
    canvas.style.transform = 'scale(1.06)';
    window.setTimeout(teardown, 720);
}

export const isBurning = (): boolean => !!canvas && !restoring;

export function startFire(opts?: { blue?: boolean; title?: string; subtitle?: string; onRestored?: () => void }): void {
    if (typeof document === 'undefined' || canvas) return;
    // Respect reduced-motion: skip the burn takeover entirely (as matrixRain/snow do).
    if (reduced()) return;
    pal = opts?.blue ? BLUE : ORANGE;
    panelTitle = opts?.title ?? '🔥 RAGE RELEASE COMPLETE';
    panelSubtitle = opts?.subtitle ?? 'The Backoffice has been cleansed by fire. No data was harmed in the process.';
    onRestored = opts?.onRestored ?? null;

    dpr = window.devicePixelRatio || 1;
    canvas = document.createElement('canvas');
    // pointer-events:auto — the burning page can't be clicked through.
    canvas.style.cssText = `position:fixed;inset:0;z-index:${Z};pointer-events:auto;`;
    document.body.appendChild(canvas);
    ctx = canvas.getContext('2d');
    if (!ctx) {
        canvas.remove();
        canvas = null;
        return;
    }
    prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden'; // lock scroll during the takeover
    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('keydown', onKey);
    embers = [];
    done = false;
    restoring = false;
    shook = false;
    phase = 0;
    startedAt = Date.now();
    raf = requestAnimationFrame(loop);
}
